/**
 * AddMembersModal - Reusable modal to select users (and optional role) and confirm additions.
 *
 * Used by:
 * - ProjectMembers (add members to a project)
 * - TeamDetails (add members to a team)
 */
import { useEffect, useMemo, useState } from 'react';
import { User } from '@/types';
import { Search, Loader2 } from 'lucide-react';

export type MemberRole = 'admin' | 'member' | 'viewer';

interface AddMembersModalProps {
  isOpen: boolean;
  title: string;
  description?: string;
  availableUsers: User[];
  loadingUsers?: boolean;

  /** Show role selector (admin / member / viewer). If false, role will be undefined in onConfirm. */
  showRoleSelector?: boolean;
  initialRole?: MemberRole;

  /** Called when user closes the modal (via X or Cancel). */
  onClose: () => void;

  /**
   * Called with selected user IDs (and optional role) when user confirms.
   * If it returns a Promise, the modal will show a loading state until resolved.
   */
  onConfirm: (selectedUserIds: string[], role?: MemberRole) => void | Promise<void>;

  /** Override confirm button label (defaults to "Add Members"). */
  confirmLabel?: string;
}

export const AddMembersModal = ({
  isOpen,
  title,
  description,
  availableUsers,
  loadingUsers,
  showRoleSelector = true,
  initialRole = 'member',
  onClose,
  onConfirm,
  confirmLabel = 'Add Members',
}: AddMembersModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [role, setRole] = useState<MemberRole>(initialRole);
  const [submitting, setSubmitting] = useState(false);

  // Reset local state when modal is opened/closed
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSelectedIds(new Set());
      setRole(initialRole);
      setSubmitting(false);
    }
  }, [isOpen, initialRole]);

  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return availableUsers;
    return availableUsers.filter((user) => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      return (
        user.email.toLowerCase().includes(q) ||
        user.username.toLowerCase().includes(q) ||
        fullName.includes(q)
      );
    });
    
  }, [availableUsers, searchQuery]);

  if (!isOpen) return null;

  const handleToggleUser = (userId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(userId);
      else next.delete(userId);
      return next;
    });
  };

  const handleConfirm = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || submitting) return;

    try {
      setSubmitting(true);
      if (showRoleSelector) {
        await onConfirm(ids, role);
      } else {
        await onConfirm(ids);
      }
      // Let parent decide whether to close, but reset local selection
      setSelectedIds(new Set());
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = selectedIds.size;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            {description && (
              <p className="text-xs text-gray-500 mt-1">{description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 flex-1 overflow-y-auto">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, email, or username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Role selection (optional) */}
          {showRoleSelector && (
            <div className="mb-4 flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Role:</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setRole('member')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium ${
                    role === 'member'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Member
                </button>
                <button
                  type="button"
                  onClick={() => setRole('admin')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium ${
                    role === 'admin'
                      ? 'bg-yellow-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Admin
                </button>
                <button
                  type="button"
                  onClick={() => setRole('viewer')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium ${
                    role === 'viewer'
                      ? 'bg-gray-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Viewer
                </button>
              </div>
            </div>
          )}

          {/* Selected Count */}
          {selectedCount > 0 && (
            <div className="mb-3 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg">
              <p className="text-sm text-primary-700">
                <strong>{selectedCount}</strong> user
                {selectedCount !== 1 ? 's' : ''} selected
              </p>
            </div>
          )}

          {/* Users list */}
          <div className="border border-gray-200 rounded-lg max-h-80 overflow-y-auto">
            {loadingUsers ? (
              <div className="flex items-center justify-center py-8 text-gray-500 gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading users...</span>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                {searchQuery
                  ? 'No users found matching your search'
                  : 'No users available to add'}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredUsers.map((user) => (
                  <label
                    key={user.id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(user.id)}
                      onChange={(e) => handleToggleUser(user.id, e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <div className="flex-shrink-0">
                      {user.avatar_url ? (
                        <img
                          className="h-8 w-8 rounded-full"
                          src={user.avatar_url}
                          alt=""
                        />
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-medium">
                          {user.username.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.display_name || `${user.first_name} ${user.last_name}`}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        @{user.username} • {user.email}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={selectedCount === 0 || submitting}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Adding...
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
};


